// Wires expo-linear-gradient into Tamagui's <LinearGradient> on native (web uses
// a CSS gradient with no setup). MUST run before any Tamagui import below, or the
// native gradient silently no-ops. Side-effect import — nothing to reference.
import '@tamagui/native/setup-expo-linear-gradient';
import { activeScrollTarget } from '@/components/ui/activeScrollTarget';
import { useNotificationRouter } from '@/src/hooks/useNotificationRouter';
import { FONT_ASSETS } from '@/src/lib/fonts';
import {
  InsetStyleProvider,
  useResolvedInsetStyle,
} from '@/src/lib/safeAreaInset';
import {
  APP_MAX_WIDTH,
  headerSlotInsetOptions,
} from '@/components/ui/pageFrame';
import { useRestoredSession } from '@/src/hooks/useRestoredSession';
import { useSessionUser } from '@/src/lib/sessionStore';
import { tamaguiConfig } from '@/tamagui.config';
import * as Sentry from '@sentry/react-native';
import { useFonts } from 'expo-font';
import { Observe, ObserveRoot, useObserve } from 'expo-observe';
import PSAlertHost from '@/components/ui/PSAlertHost';
import { PSModalHost } from '@/components/ui/PSModal';
import PSToastHost from '@/components/ui/PSToastHost';
import { Stack, useSegments } from 'expo-router';
import Head from 'expo-router/head';
import * as SplashScreen from 'expo-splash-screen';
import { StatusBar } from 'expo-status-bar';
import { useEffect, useRef } from 'react';
import {
  Platform,
  type View as RNViewType,
  View as RNView,
} from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { KeyboardProvider } from 'react-native-keyboard-controller';
import { SafeAreaProvider, SafeAreaView } from 'react-native-safe-area-context';
import { getTokenValue, TamaguiProvider, View } from 'tamagui';

// Web autoplay refusal from expo-video's un-caught play() promise (Sentry
// already ignores it). Expo's dev overlay would otherwise take over the screen
// for it, with a bogus source frame. Capture-phase + stopImmediate so the
// overlay's own listener never sees it.
if (Platform.OS === 'web' && typeof window !== 'undefined') {
  window.addEventListener(
    'unhandledrejection',
    (e) => {
      if ((e.reason as Error | undefined)?.name === 'NotAllowedError') {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    },
    true
  );
}

// Web build serves pre-flattened Tamagui styles from tamagui.generated.css
// (produced by `npm run generate:css`, which runs automatically before start/
// web/export:web). Guarded to web so native — which styles at runtime — is
// untouched. Without this, theme/token CSS vars (e.g. $brand) don't resolve on
// web and token-driven colors render wrong.
if (Platform.OS === 'web') {
  // A conditional, side-effect CSS import must be a lazy require: a top-level
  // ESM `import` can't be web-guarded and would fail to resolve on native.
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  require('@/tamagui.generated.css');
  // Restores RN's default `position: relative` on views and text, which Tamagui
  // v2 drops on web — see the file. Zero-specificity, so load order is moot.
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  require('@/tamagui.web-reset.css');
}

Sentry.init({
  dsn: 'https://011d868832e52e204e44465613c496e5@o4510514399805440.ingest.us.sentry.io/4510891087691776',
  enabled: !__DEV__,
  // Set per EAS environment for native and OTA, and on the Render service for
  // web — one mechanism for all three, which `Updates.channel` can't be: it's
  // empty on web, so prod-web and preview-web would share an environment.
  //
  // Falls back to 'unset', NOT to 'production'. A preview build whose env didn't
  // load would otherwise file staging crashes as production ones, which is the
  // exact mixing this exists to prevent — the same reasoning as api.ts's refusal
  // to default BASE_URL. `||` rather than `??` because Babel inlines an unset
  // EXPO_PUBLIC_* as an empty string, which is as wrong as missing.
  environment: __DEV__
    ? 'development'
    : process.env.EXPO_PUBLIC_SENTRY_ENV || 'unset',
  ignoreErrors: [
    /Java object is gone/i,
    // Noise injected by browser extensions (e.g. password managers); not an app error.
    /Object Not Found Matching Id:\d+, MethodName:update/i,
    // expo-video's web player (VideoPlayer.web.tsx) calls the underlying
    // HTMLVideoElement.play() and never attaches a .catch() to the promise it
    // returns. Every autoplay reel (AccessPassShowreel, PSUpsellBanner) gets
    // interrupted by a pause()/unmount/focus-change before that promise
    // settles often enough that these are some of the top unhandled-rejection
    // issues in Sentry — but the promise lives inside the library, unreachable
    // from our call sites, so there's nothing here to fix. Fix upstream
    // (expo-video) or drop these once that's patched.
    /The play\(\) request was interrupted/i,
    /The fetching process for the media resource was aborted/i,
    /The element has no supported sources\.?/i,
    /Failed to load because no supported source was found\.?/i,
    /The operation was aborted/i,
    // Same unhandled-rejection source as above: the browser blocks the
    // autoplay play() call outright (no user gesture yet / permission
    // denied) instead of interrupting it mid-flight.
    /The request is not allowed by the user agent or the platform/i,
    // Instagram's in-app browser injects its own navigation-performance
    // logger (iabjs://navigation_performance_logger_android) that calls a
    // native Java bridge method; on some devices that invocation itself
    // throws. Entirely inside Instagram's injected script, not our code.
    /Error invoking postMessage: Java exception was raised during method invocation/i,
    // expo-font's web loader (FontLoader.web.ts) wraps ExpoFontLoader.loadAsync
    // in a try/catch to swallow fontfaceobserver's timeout rejection, but the
    // catch only fires on a synchronous throw — the promise still rejects
    // asynchronously after fontfaceobserver's hardcoded 12000ms, uncaught. The
    // font itself already rendered via the injected stylesheet by then (that's
    // the whole point of the try/catch), so this is a verification-only
    // failure, not a missing font. Fix upstream (expo-font) or drop this once
    // that's patched — same shape as the expo-video entries above.
    //
    // Matched by timeout value (currently always 12000, hardcoded in
    // ExpoFontLoader.web.ts) rather than the literal string, so this survives
    // expo-font changing that constant. Word order ("Xms timeout exceeded")
    // is fontfaceobserver's alone — axios says "timeout of Xms exceeded" — so
    // this can't also swallow a real api.ts request timeout.
    /\d+ms timeout exceeded/i,
  ],

  // Adds more context data to events (IP address, cookies, user, etc.)
  // For more information, visit: https://docs.sentry.io/platforms/react-native/data-management/data-collected/
  sendDefaultPii: true,

  // Enable Logs
  enableLogs: false,

  // Enable traces
  tracesSampleRate: 0.2,

  // uncomment the line below to enable Spotlight (https://spotlightjs.com)
  // spotlight: __DEV__,
});

SplashScreen.preventAutoHideAsync();

// Must run before any screen mounts — tags Observe's route metrics with the
// route pattern (e.g. "/events/[id]") instead of only app-wide aggregates.
Observe.configure({
  integrations: { 'expo-router': true },
});

// ── REHAUL BASELINE ────────────────────────────────────────────────
// Trimmed to the retained infra: Sentry, analytics, notification routing,
// gesture root. The old UserProvider/AlertHost and every deleted route's
// <Stack.Screen> entry were removed with the old app.
//
// Single hardcoded theme "light" (no light/dark switching — the name is a
// color scheme so web CSS generates a valid theme selector; see tamagui.config).
// Design-system fonts (GeneralSans + Gochi Hand) are loaded here via expo-font
// before the UI shows.
//
// The session is real and backed by the auth API, with the token in AsyncStorage
// and the user in a module-scope store (src/lib/sessionStore.ts) — no provider,
// because api.ts's 401 interceptor has to be able to drop it from outside React.
// Screens are file-discovered (no per-route <Stack.Screen> list needed) EXCEPT
// the auth group, which needs one explicit entry to present modally — see
// AppFrame.
// ───────────────────────────────────────────────────────────────────

function RootLayout() {
  useNotificationRouter();

  // Load the design-system fonts. `error` is treated like `loaded` so a font
  // failure doesn't wedge the splash forever (text falls back to system).
  const [loaded, error] = useFonts(FONT_ASSETS);

  // Who's signed in — one `GET /me` against the stored token, written straight
  // into the session store. Both hooks start on the SAME mount, so the network
  // call overlaps font loading instead of queueing behind it; the boot costs
  // whichever is slower, not their sum.
  const isSessionRestored = useRestoredSession();

  // The splash gate. Everything the first frame depends on has to be settled
  // before the splash lifts, or the user watches the app assemble itself:
  // unstyled text snapping to the brand font, a "Sign up" button becoming an
  // account glyph. Held here, the first thing they see is already correct.
  const isReady = (loaded || error !== null) && isSessionRestored;

  const { markInteractive } = useObserve();

  useEffect(() => {
    if (isReady) {
      SplashScreen.hideAsync();
      markInteractive();
    }
  }, [isReady, markInteractive]);

  if (!isReady) {
    return null;
  }

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      {/* Root-level because [[PageScrollView]] reads its context on every page.
          On web the library resolves to its own no-op build (Expo's Metro turns
          off `.native` resolution there), so this is a bare View. */}
      <KeyboardProvider>
        <SafeAreaProvider style={{ flex: 1 }}>
          <TamaguiProvider config={tamaguiConfig} defaultTheme="light">
            {/* Owns the inset variant so the SafeAreaView + StatusBar below can
                read it. A screen calls useInsetStyle('dark') to override. */}
            <InsetStyleProvider>
              {/* No session provider: the user lives in a module-scope store
                  (src/lib/sessionStore.ts) so api.ts's 401 interceptor can drop
                  it without a React tree to reach into. The cold-start restore
                  above has already written to it by the time this renders — the
                  splash gate is what guarantees it. */}
              <AppFrame />
            </InsetStyleProvider>
          </TamaguiProvider>
        </SafeAreaProvider>
      </KeyboardProvider>
    </GestureHandlerRootView>
  );
}

export default Sentry.wrap(ObserveRoot.wrap(RootLayout));

/**
 * The safe-area strip + status bar + Stack. Split out from RootLayout so it can
 * live INSIDE <InsetStyleProvider> and read the current inset variant — the
 * strip color and status-bar glyph style both follow whatever variant the
 * active screen selected (default `'light'`).
 *
 * Every screen renders a NATIVE stack header (headers are on by default here —
 * see `screenOptions.headerShown`), and a native header draws its OWN top-notch
 * inset. So this root SafeAreaView takes NO top edge (`edges={[]}`): the header
 * owns the notch uniformly on every screen, which is what keeps navigation from
 * shifting the layout (a mix of inset / no-inset screens is what jars). The
 * SafeAreaView stays only to paint the strip background color behind the notch.
 */
function AppFrame() {
  const { backgroundColor, statusBarStyle } = useResolvedInsetStyle();
  const user = useSessionUser();
  // Which host draws the toasts — see the PSToastHost render below. Both
  // natively-presented modal groups mount their own, so this gate covers both.
  const segments = useSegments();
  const inModalFlow =
    segments[0] === 'auth' || segments[0] === 'delete-account';

  return (
    <AppColumn>
      <SafeAreaView style={{ flex: 1, backgroundColor }} edges={[]}>
        {/* Fallback browser tab title — expo-router's automatic web
            documentTitle sync is disabled in this version, so each screen
            sets its own via <Head> (see e.g. app/profile/index.tsx). This one
            covers screens that don't, and shows while a screen's <Head>
            hasn't mounted yet (e.g. a loading state). */}
        <Head>
          <title>Putting Scene</title>
        </Head>
        <StatusBar style={statusBarStyle} />
        <Stack
          screenOptions={{
            ...headerSlotInsetOptions,
            // Headers ON by default: a new screen is safe-by-default (its native
            // header owns the notch). Screens override only header CONTENT
            // (headerLeft/Right/Title/Style), not whether it shows.
            headerShown: true,
            headerShadowVisible: false,
            contentStyle: {
              backgroundColor: getTokenValue('$color.background'),
              // paddingHorizontal: getTokenValue('$space.xl'),
            },
            gestureEnabled: true,
            animation: 'slide_from_right',
            // statusBarStyle: statusBarStyle,
          }}
        >
          {/* The one explicit route entry: the auth flow is a dismissible SHEET
              over Home (Airbnb-style). `presentation: 'modal'` must be declared
              here on the PARENT stack — the nested app/auth/_layout.tsx Stack
              can't make itself modal. `headerShown: false` hands the header to
              that nested Stack, which owns the per-step ✕/← controls. Every
              other screen is still file-discovered with no entry needed. */}
          <Stack.Screen
            name="auth"
            options={{
              presentation: 'modal',
              headerShown: false,
              gestureEnabled: false,
            }}
          />
          {/* An event's routes have their own nested Stack (app/event/[id]/
              _layout.tsx — it mounts the event + cart provider, which needs the
              id, hence the layout sitting inside the dynamic segment). A nested
              Stack draws its OWN header, so without this the root Stack would
              draw a SECOND one on top (the "index / event" bar).
              `headerShown: false` hands the header to the nested Stack, which
              each event screen customises (the detail's transparent floating
              controls, the confirm step's back button) — same handoff as the
              `auth` group above. */}
          <Stack.Screen name="events/[id]" options={{ headerShown: false }} />
          {/* The cancel confirm, lifted as a sheet over the membership it's
              about — `presentation: 'modal'` can only be declared by the parent
              stack, so a screen can't do this from its own `<Stack.Screen>`.
              Unlike `auth` and `delete-account` there's no nested Stack here, so
              the header stays ON and app/my-memberships/[id]/cancel.tsx sets its
              own ✕ against this same root header. */}
          <Stack.Screen
            name="my-memberships/[id]/cancel"
            options={{ presentation: 'modal' }}
          />
          <Stack.Protected guard={!!user}>
            <Stack.Screen name="profile/index" />
            <Stack.Screen name="settings" />
            {/* Edit profile is a sheet, not a page, because it's reached from
                more than one place — the profile screen, the settings row, and
                anywhere else that finds a detail missing. A modal returns the
                user to wherever they came from on dismiss, so no caller has to
                know what to navigate back to. Same shape as
                `my-memberships/[id]/cancel`: no nested Stack, so the header
                stays ON and app/profile/edit.tsx sets its own ✕ against it. */}
            <Stack.Screen
              name="profile/edit"
              options={{ presentation: 'modal' }}
            />
            {/* A modal over Settings, same handoff as `auth`: this parent
                declares the presentation, the nested Stack draws the header.
                Inside the guard on purpose — you can't delete an account you
                aren't signed into, and it's the guard flipping that dismisses
                the flow once the delete lands. */}
            <Stack.Screen
              name="delete-account"
              options={{ presentation: 'modal', headerShown: false }}
            />
          </Stack.Protected>
          {/* The dev component gallery — guarded on `__DEV__`, not signed-in
              state, so it's a sibling of the `!!user` guard above rather than
              nested inside it. `__DEV__` is true only when Metro serves the
              bundle from a local dev server (see app/index.tsx, which already
              uses it to make the gallery's entry point inert), so a preview or
              production build has no such route at all. `Stack.Protected`
              rather than a redirect inside the group: the route genuinely
              shouldn't exist in a shipped build, and a guard removes it from
              the navigator instead of letting it mount and then bounce.
              `headerShown: false` hands the header to the nested Stack
              (app/components/_layout.tsx), same handoff as `auth` and
              `events/[id]` above. One entry for the whole group, so the ~30
              routes the gallery is about to split into need no further
              registration here. */}
          <Stack.Protected guard={__DEV__}>
            <Stack.Screen name="components" options={{ headerShown: false }} />
          </Stack.Protected>
        </Stack>
        {/* The app-wide toast host, a sibling of the Stack so it paints over
            every screen in it.

            There are TWO hosts (this one and the auth flow's), and the gate is
            what keeps them from both drawing the same toast: @tamagui/toast
            publishes to ONE module-level observer that every mounted host
            subscribes to, so two mounted at once means the user reads the
            message twice on web and Android. Mutually exclusive by route
            instead — the auth group has its own host because on iOS it's a
            natively-presented sheet and THIS one renders underneath it (see
            PSToastHost). Anywhere else, this is the only host there is. */}
        {/*TODO: this feels off. why can't there be just one toast host? */}
        {!inModalFlow && <PSToastHost />}
        {/* Unconditional, unlike the toast host: showAlert queues to one
            module-level emitter and this is its only subscriber, so there is
            no second host to double up with. Null off web — see the file. */}
        <PSAlertHost />
        <PSModalHost />
      </SafeAreaView>
    </AppColumn>
  );
}

/**
 * The app column: on a desktop browser the app draws at phone width, centred,
 * with the window's leftover width painted in the page background.
 *
 * Wraps the SafeAreaView rather than sitting inside it, so the strip colour a
 * screen picks (the dark hero's, say) stays inside the column and the margins
 * keep one neutral colour across every route.
 *
 * A no-op off web — see APP_MAX_WIDTH.
 *
 * The margin painted around the column has no scrollable content of its own,
 * so a wheel event over it would otherwise do nothing. This forwards it to
 * whichever screen is focused (published to [[activeScrollTarget]] by
 * PageScrollView/PageFlatList) whenever the event lands outside the column.
 */
function AppColumn({ children }: { children: React.ReactNode }) {
  const columnRef = useRef<RNViewType>(null);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const onWheel = (e: WheelEvent) => {
      const columnNode = columnRef.current as unknown as HTMLElement | null;
      if (!columnNode || columnNode.contains(e.target as Node)) return;
      activeScrollTarget.current?.scrollBy({ top: e.deltaY, left: e.deltaX });
    };
    window.addEventListener('wheel', onWheel);
    return () => window.removeEventListener('wheel', onWheel);
  }, []);

  if (Platform.OS !== 'web') return <>{children}</>;
  return (
    <View flex={1} items="center" bg="$color.background">
      {/* Plain RN View: pure layout, no design values — and Tamagui's strict
          token mode rejects a raw "100%" on `width`. */}
      <RNView
        ref={columnRef}
        style={{ flex: 1, width: '100%', maxWidth: APP_MAX_WIDTH }}
      >
        {children}
      </RNView>
    </View>
  );
}
